import React, { useState, useRef, forwardRef, useImperativeHandle } from 'react';
import { useTranslation } from 'react-i18next';
import WarehouseOverview from './WarehouseOverview';
import InventoryManagement from './InventoryManagement';
import ReceivingOperations from './ReceivingOperations';
import ShippingOperations from './ShippingOperations';
import ReturnsAndRequisitions from './ReturnsAndRequisitions';
import GoodsReceiptManager from './GoodsReceiptManager';
import PickListViewer from '../../components/PickLists/PickListViewer';
import './WarehouseDashboard.css';

const WarehouseDashboard = forwardRef(({
  externalTabControl = false,
  externalActiveTab = null,
  externalActiveSubTab = null,
  onExternalNavigate = null,
  openItem = null,
  onItemClosed = null,
  navOptions = null
}, ref) => {
  const { t } = useTranslation('warehouse');
  const [activeTab, setActiveTab] = useState('overview');
  const [activeSubTab, setActiveSubTab] = useState('dashboard');
  const [internalNavOptions, setInternalNavOptions] = useState(null);
  const [selectedPickListId, setSelectedPickListId] = useState(null);
  // GoodsReceiptManager has no refresh(): remount it instead
  const [finishedGoodsKey, setFinishedGoodsKey] = useState(0);

  // Refs for child components
  const overviewRef = useRef();
  const inventoryRef = useRef();
  const receivingRef = useRef();
  const shippingRef = useRef();
  const returnsRef = useRef();

  // Use external tab state when provided
  const currentActiveTab = externalTabControl ? externalActiveTab : activeTab;
  const currentActiveSubTab = externalTabControl ? externalActiveSubTab : activeSubTab;

  // Map tab IDs to component refs for refresh delegation
  const tabRefMap = {
    'overview': overviewRef,
    'inventory-mgmt': inventoryRef,
    'receiving-ops': receivingRef,
    'shipping-ops': shippingRef,
    'returns-reqs': returnsRef,
  };

  const handleRefresh = () => {
    if (currentActiveTab === 'receiving-ops' && currentActiveSubTab === 'finished-goods') {
      setFinishedGoodsKey(key => key + 1);
      return;
    }
    const activeRef = tabRefMap[currentActiveTab];
    if (activeRef?.current?.refresh) {
      activeRef.current.refresh();
    }
  };

  // Expose refresh method to parent component
  useImperativeHandle(ref, () => ({
    refresh: handleRefresh
  }));

  // Navigation callback for WarehouseOverview quick-links (options: e.g. { stockFilter: 'low' })
  const handleNavigate = (tab, subTab, options = null) => {
    if (externalTabControl) {
      // parent controls tabs
      if (onExternalNavigate) onExternalNavigate(tab, subTab, options);
      return;
    }
    setActiveTab(tab);
    setActiveSubTab(subTab);
    setInternalNavOptions(options);
  };

  const currentNavOptions = externalTabControl ? navOptions : internalNavOptions;

  // Callback when overview wants to view a pick list: open that pick list, not the generic list
  const handleViewPickList = (pickList) => {
    if (pickList?.pick_list_id) {
      setSelectedPickListId(pickList.pick_list_id);
    } else {
      handleNavigate('shipping-ops', 'picking');
    }
  };

  // Callback when receiving wants to create a supplier return: open the create dialog for that GRN line
  const handleCreateReturn = (grnItem) => {
    handleNavigate('returns-reqs', 'returns', grnItem ? { returnGRNItem: grnItem } : null);
  };

  // Tab configuration
  const tabConfig = {
    'overview': {
      name: 'Overview',
      icon: '\u{1F4CA}',
      subtabs: {
        'dashboard': { name: 'Dashboard', icon: '\u{1F3E0}' }
      }
    },
    'inventory-mgmt': {
      name: 'Inventory Management',
      icon: '\u{1F4E6}',
      subtabs: {
        'inventory': { name: 'Materials Inventory', icon: '\u{1F4CA}' },
        'storage': { name: 'Inventory Transfers', icon: '\u{1F504}' },
        'formulations': { name: 'Ink Formulations', icon: '\u{1F9EA}' }
      }
    },
    'receiving-ops': {
      name: 'Receiving Operations',
      icon: '\u{1F4E5}',
      subtabs: {
        'incoming': { name: 'Incoming Deliveries', icon: '\u{1F4E6}' },
        'receipt': { name: 'Goods Receipt', icon: '\u{1F4CB}' },
        'quality': { name: 'Quality Check', icon: '\u{1F50D}' },
        'finished-goods': { name: 'Finished Goods Receipt', icon: '\u{1F3ED}' }
      }
    },
    'shipping-ops': {
      name: 'Shipping Operations',
      icon: '\u{1F4E4}',
      subtabs: {
        'picking': { name: 'Pick Lists', icon: '\u{1F4DD}' },
        'packing': { name: 'Packing', icon: '\u{1F4E6}' },
        'shipping': { name: 'Sales Order Shipping', icon: '\u{1F6A2}' }
      }
    },
    'returns-reqs': {
      name: 'Returns & Requisitions',
      icon: '\u{1F504}',
      subtabs: {
        'requisitions': { name: 'Material Requisitions', icon: '\u{1F4CB}' },
        'returns': { name: 'Supplier Returns', icon: '\u21A9\uFE0F' },
        'material-returns': { name: 'Material Returns', icon: '\u{1F4E5}' }
      }
    }
  };

  const renderActiveComponent = () => {
    switch (currentActiveTab) {
      case 'overview':
        return (
          <WarehouseOverview
            ref={overviewRef}
            onNavigate={handleNavigate}
            onViewPickList={handleViewPickList}
          />
        );
      case 'inventory-mgmt':
        return (
          <InventoryManagement
            ref={inventoryRef}
            activeSubTab={currentActiveSubTab}
            openItem={openItem}
            onItemClosed={onItemClosed}
            initialStockFilter={currentNavOptions?.stockFilter}
          />
        );
      case 'receiving-ops':
        // Finished goods from production (same screen as the supervisor's Home → Production)
        if (currentActiveSubTab === 'finished-goods') {
          return <GoodsReceiptManager key={finishedGoodsKey} />;
        }
        return (
          <ReceivingOperations
            ref={receivingRef}
            activeSubTab={currentActiveSubTab}
            onCreateReturn={handleCreateReturn}
          />
        );
      case 'shipping-ops':
        return (
          <ShippingOperations
            ref={shippingRef}
            activeSubTab={currentActiveSubTab}
          />
        );
      case 'returns-reqs':
        return (
          <ReturnsAndRequisitions
            ref={returnsRef}
            activeSubTab={currentActiveSubTab}
            initialReturnGRNItem={currentNavOptions?.returnGRNItem}
          />
        );
      default:
        return (
          <InventoryManagement
            ref={inventoryRef}
            activeSubTab={currentActiveSubTab}
          />
        );
    }
  };

  return (
    <div className="warehouse-dashboard flex-container">
      {!externalTabControl && (
        <div className="warehouse-navigation">
          {/* Main Tabs */}
          <div className="main-tabs">
            {Object.entries(tabConfig).map(([tabId, tab]) => (
              <button
                key={tabId}
                className={`main-tab ${activeTab === tabId ? 'active' : ''}`}
                onClick={() => {
                  setActiveTab(tabId);
                  setInternalNavOptions(null);
                  const firstSubTabId = Object.keys(tab.subtabs)[0];
                  setActiveSubTab(firstSubTabId);
                }}
              >
                <span className="tab-icon">{tab.icon}</span>
                <span className="tab-label">{tab.name}</span>
              </button>
            ))}
          </div>

          {/* Sub Tabs */}
          <div className="sub-tabs">
            <div className="subtabs-left">
              {tabConfig[activeTab] && Object.entries(tabConfig[activeTab].subtabs).map(([subTabId, subTab]) => (
                <button
                  key={subTabId}
                  className={`sub-tab ${activeSubTab === subTabId ? 'active' : ''}`}
                  onClick={() => { setActiveSubTab(subTabId); setInternalNavOptions(null); }}
                >
                  <span className="subtab-icon">{subTab.icon}</span>
                  <span className="subtab-label">{subTab.name}</span>
                </button>
              ))}
            </div>
            <button
              className="btn-refresh-subtab"
              onClick={handleRefresh}
            >
              {'\u{1F504}'} {t('common:ui.refresh')}
            </button>
          </div>
        </div>
      )}

      <div className="warehouse-content flex-content">
        {renderActiveComponent()}
      </div>

      {selectedPickListId && (
        <PickListViewer
          pickListId={selectedPickListId}
          onClose={() => {
            setSelectedPickListId(null);
            if (overviewRef.current?.refresh) overviewRef.current.refresh();
          }}
        />
      )}
    </div>
  );
});

WarehouseDashboard.displayName = 'WarehouseDashboard';

export default WarehouseDashboard;
